import { useContext, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  Divider,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useATS } from "../context/ATSContext";
import { jobService, templateService } from "../services/jobService";
import JdAttachmentField from "./JdAttachmentField";
import EmailListField from "./EmailListField";
import { AutoTaskAuthContext } from "../../../Context/AutoTaskContext";

const TEAL = {
  50: "#e6f7f5",
  100: "#ccefeb",
  200: "#99dfd7",
  300: "#66cfc3",
  400: "#33bfaf",
  500: "#0c8a9b",
  600: "#0a7a8a",
  700: "#086a78",
  800: "#065a66",
  900: "#044a54",
};

const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";
// AI_INTERVIEW was missing from this list, and that omission is the whole
// reason the pipeline kept saying "this job has no AI interview stage". The
// stage type existed in the database and in the API; there was simply no way to
// pick it in the one screen where stages get created.
const stagesTypes = [
  "SCREENING",
  "AI_INTERVIEW",
  "TECHNICAL",
  "HR_ROUND",
  "ASSESSMENT",
  "FINAL",
  // Administrative, not evaluative. Added with the six default rounds; see
  // DEFAULT_STAGES and SCORELESS_TYPES below.
  "OFFER",
  "JOINING",
];

// Human labels for the type dropdown. The raw enum values were shown before,
// so the picker read "AI_INTERVIEW" and "HR_ROUND" — which is the database's
// vocabulary, not a recruiter's.
const STAGE_TYPE_LABELS = {
  SCREENING: "Resume Screening",
  AI_INTERVIEW: "AI / Call Interview",
  TECHNICAL: "Technical",
  HR_ROUND: "HR Round",
  ASSESSMENT: "Assessment",
  FINAL: "Final",
  OFFER: "Offer",
  JOINING: "Joining",
};

// ─── The rounds every job starts with ────────────────────────────────────────
//
// A job used to open with ONE round and the recruiter built the rest by hand,
// which meant no two jobs had the same pipeline and cross-job reporting
// compared rounds that were not the same round. These six are the process as
// actually run, so the common case is now zero clicks.
//
// Fully editable: rename, retype, reorder, add, remove. This is a starting
// point, not a schema.
//
// ⚠️ Exactly ONE round is typed AI_INTERVIEW, and that is deliberate. The
// interview invite resolves the job's first AI_INTERVIEW stage
// (interviewController.createInvite) and rubrics attach only to that type, so a
// second one would make "which round does Send link use?" depend on
// stage_order. Remove it and the job simply cannot issue interview links — the
// candidate pipeline says so in an alert rather than failing silently.
const DEFAULT_STAGES = [
  { stage_name: "Resume Screening", stage_type: "SCREENING" },
  { stage_name: "AI/Call Interview", stage_type: "AI_INTERVIEW" },
  { stage_name: "F2F Technical", stage_type: "TECHNICAL" },
  { stage_name: "HR Round", stage_type: "HR_ROUND" },
  { stage_name: "Offer", stage_type: "OFFER" },
  { stage_name: "Joining", stage_type: "JOINING" },
];

// Rounds that carry no score and no pass mark.
//
// An offer is extended or it is not; a joining date happens or it does not.
// Neither is a thing you score out of 50, so the wizard shows no score inputs
// for them and nothing downstream should expect a number.
const SCORELESS_TYPES = new Set(["OFFER", "JOINING"]);

const isScoreless = (type) => SCORELESS_TYPES.has(type);

// Builds the initial stage list. A function rather than a constant because
// useState would otherwise hand every instance the SAME nested objects, and
// editing one job's rounds would mutate the defaults for the next one opened.
const buildDefaultStages = () =>
  DEFAULT_STAGES.map((s, idx) => ({
    stage_name: s.stage_name,
    stage_order: idx + 1,
    stage_type: s.stage_type,
    // Elimination only where a candidate can actually be eliminated. Nothing
    // reads this column today, so it is documentation of intent rather than
    // behaviour — but a truthful default costs nothing.
    is_elimination_round: !isScoreless(s.stage_type),
    // ── Both zero, and no longer asked for ─────────────────────────────────
    //
    // These were required inputs on every round, and the numbers were invented:
    // nobody knew what "max 50, passing 30" meant for an F2F panel, so they
    // typed something to get past the validation. A number that exists only to
    // satisfy a form is worse than no number — it looks like a threshold
    // somebody chose. Scoring lives in the interview rubric, which is where a
    // real one is set.
    max_score: 0,
    passing_score: 0,
    questions: [],
  }));

// ─── The merged first round ──────────────────────────────────────────────────
//
// HR screening and AI screening were two stages, and treating them as two was
// wrong in practice: the same questions, the same candidates, the same decision.
// A candidate sat in "HR Screening" and could not be sent an interview link,
// because the link needs an AI_INTERVIEW stage and that was the NEXT round.
//
// They are now one round — "HR/AI Screening", typed AI_INTERVIEW — where the
// resume ranking and the interview invitation live side by side. HR reads the
// ranking and sends the link from the same row.
//
// Typed AI_INTERVIEW rather than SCREENING because that is the type the rest of
// the system already keys interviewing off: interviewController resolves the
// stage to invite into, and interview rubrics attach only to AI_INTERVIEW. The
// screening behaviour is the part that had to widen, which is what the
// predicate below does.

/**
 * Does this round carry screening questions?
 *
 * True for the merged round and for a plain SCREENING stage, so jobs created
 * before this change keep working unchanged — their SCREENING stage still shows
 * its questions and still validates. Every question-related branch in this file
 * asks this rather than comparing to a literal, because there are eleven of them
 * and the one that gets missed is the one that silently drops the questions on
 * save.
 */
const carriesQuestions = (type) =>
  type === "SCREENING" || type === "AI_INTERVIEW";

// ─── Which round the reviewed questions belong to ────────────────────────────
//
// `carriesQuestions` below is true for BOTH a SCREENING and an AI_INTERVIEW
// round, and the six defaults now contain one of each. Left as it was, the
// prefill wrote the template's questions into both and step 3 listed the same
// questions twice under two headings — so the questions looked duplicated and
// it was unclear which round would ask them.
//
// One round owns them: the AI/Call Interview, because that is the round that
// actually asks. Falls back to whichever round carries questions if a job has
// no AI round at all (removing it is allowed), and -1 when none does.
const questionRoundIndex = (list) => {
  const ai = list.findIndex((s) => s.stage_type === "AI_INTERVIEW");
  if (ai !== -1) return ai;
  return list.findIndex((s) => carriesQuestions(s.stage_type));
};

// ─── Stepper ─────────────────────────────────────────────────────────────────
const CustomStepper = ({ activeStep, steps }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      bgcolor: "#fff",
      p: 1.6,
      borderRadius: 0.4,
    }}
  >
    {steps.map((label, idx) => {
      const completed = idx < activeStep;
      const active = idx === activeStep;

      return (
        <Box
          key={label}
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
          }}
        >
          {/* Step pill / item */}
          <Box
            sx={{
              width: "100%",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: active ? 2 : 1.5,
              py: active ? 0.9 : 0.7,
              borderRadius: "10px",
              bgcolor: active ? TEAL[600] : "transparent",
              border: active ? "none" : "none",
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
          >
            {/* Number circle */}
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: active
                  ? "rgba(255,255,255,0.25)"
                  : completed
                  ? TEAL[500]
                  : "#e5e7eb",
                color: active ? "#fff" : completed ? "#fff" : "#9ca3af",
                fontWeight: 700,
                fontSize: 12,
                fontFamily: fontFam,
                flexShrink: 0,
              }}
            >
              {completed ? (
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8l3.5 3.5L13 5"
                    stroke="white"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                idx + 1
              )}
            </Box>

            {/* Label */}
            <Typography
              sx={{
                fontFamily: fontFam,
                fontSize: 13,
                fontWeight: active ? 700 : completed ? 600 : 500,
                color: active ? "#fff" : completed ? TEAL[600] : "#6b7280",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>
        </Box>
      );
    })}
  </Box>
);

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: TEAL[300] },
    "&.Mui-focused fieldset": { borderColor: TEAL[500], borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": {
    fontFamily: fontFam,
    fontSize: 12,
    color: "#64748b",
    "&.Mui-focused": { color: TEAL[600] },
  },
  "& .MuiInputBase-input": {
    fontFamily: fontFam,
    fontSize: 13,
    color: "#1e293b",
  },
};

const labelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: "#374151",
  fontFamily: fontFam,
  mb: 0.5,
  display: "block",
};

// ─── Main Component ───────────────────────────────────────────────────────────
const JobCreationForm = ({
  job,
  onCreated,
  onUpdated,
  onCancel,
  // Optional, both undefined from the dashboard. Set when the job is being
  // opened from a level in a department's hierarchy: the template is applied on
  // mount, and the department is stamped onto the job. The existing call site
  // passes neither, so its create and edit paths never enter this branch.
  initialTemplateId,
  deptContext,
}) => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const user_id = userInfo?.id;
  const { store_id } = useATS();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateQuestions, setTemplateQuestions] = useState([]);

  const [skillInput, setSkillInput] = useState("");
  const [skills, setSkills] = useState([]);

  const [jobData, setJobData] = useState({
    title: "",
    job_code: "",
    employment_type: "FULL_TIME",
    experience_required: "",
    vacancies: 1,
    description: "",
    responsibilities: "",
    required_skills: "",
    salary_min: "",
    salary_max: "",
    location: "",
    whatsapp_group_link: "",
    google_form_link: "",
    google_sheet_id: "",
    // Who else is copied on this job's candidate email, comma-separated, and
    // the sign-off it carries. Both empty by default, which the server reads as
    // "nothing configured here" and falls back to the deployment's own CC — so
    // a job saved without touching either behaves exactly as it did before.
    notify_cc: "",
    mail_signature: "",
    total_rounds: 3,
    status: "ACTIVE",
  });

  // The JD document, as { url, name } or null. Separate from jobData because it
  // is not a text field handleJobFieldChange can serve, and because it is
  // already in blob storage by the time it gets here — JdAttachmentField trades
  // the file for a URL when it is picked, so this holds a value, not a File.
  const [jd, setJd] = useState(null);

  // Every job opens with the six standard rounds — see DEFAULT_STAGES. All of
  // them editable; the first one cannot be removed, because a job with no
  // opening round has nowhere for an applicant from the intake form to land.
  const [stages, setStages] = useState(buildDefaultStages);

  const isEditMode = !!job;

  // How many rounds claim to be the AI interview. Drives the two warnings in
  // step 2 — see the note there for why neither is an error.
  const aiRoundCount = stages.filter(
    (s) => s.stage_type === "AI_INTERVIEW",
  ).length;
  // "Publish" in the stepper while the panel called itself "Screening
  // Questions" — the same step under two names. It reviews and publishes.
  const steps = ["Job Details", "Hiring Stages", "Review & Publish"];

  // ── Load job for edit ──
  useEffect(() => {
    if (isEditMode && job?.id) {
      const fetchJobData = async () => {
        try {
          setLoading(true);
          const jobRes = await jobService.getJobById(job.id);
          const jobDetails = jobRes.data.data;
          setJobData({
            title: jobDetails.title || "",
            job_code: jobDetails.job_code || "",
            employment_type: jobDetails.employment_type || "FULL_TIME",
            experience_required: jobDetails.experience_required || "",
            vacancies: jobDetails.vacancies || 1,
            description: jobDetails.description || "",
            responsibilities: jobDetails.responsibilities || "",
            required_skills: jobDetails.required_skills || "",
            salary_min: jobDetails.salary_min || "",
            salary_max: jobDetails.salary_max || "",
            location: jobDetails.location || "",
            whatsapp_group_link: jobDetails.whatsapp_group_link || "",
            google_form_link: jobDetails.google_form_link || "",
            google_sheet_id: jobDetails.google_sheet_id || "",
            notify_cc: jobDetails.notify_cc || "",
            mail_signature: jobDetails.mail_signature || "",
            total_rounds: jobDetails.total_rounds || 3,
            status: jobDetails.status || "ACTIVE",
          });
          setJd(
            jobDetails.jd_pdf_url
              ? { url: jobDetails.jd_pdf_url, name: jobDetails.jd_pdf_name }
              : null,
          );
          if (jobDetails.required_skills) {
            setSkills(
              jobDetails.required_skills
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            );
          }
          const stagesRes = await jobService.getStagesByJob(job.id);
          const stagesList = stagesRes.data.data || [];
          let screeningQuestions = [];
          if (jobDetails.job_template_id) {
            try {
              const qRes = await jobService.getScreeningQuestionsForJob(job.id);
              screeningQuestions = qRes.data.data || [];
            } catch (err) {
              console.warn(err);
            }
            if (screeningQuestions.length === 0) {
              try {
                const tRes = await templateService.getTemplateById(
                  jobDetails.job_template_id,
                );
                screeningQuestions = tRes.data.data?.questions || [];
              } catch (err) {
                console.warn(err);
              }
            }
          }
          // The round that owns the reviewed questions. Was "the first round
          // that could carry questions", which on the new six-round default is
          // Resume Screening — so the questions the AI interview asks were
          // listed under the round that does not ask them.
          const screeningIdx = questionRoundIndex(
            stagesList.map((s) => ({ stage_type: s.stage_type })),
          );
          setStages(
            stagesList.map((stage, idx) => ({
              id: stage.id,
              stage_name: stage.stage_name,
              stage_order: stage.stage_order,
              stage_type: stage.stage_type,
              is_elimination_round: stage.is_elimination_round,
              max_score: stage.total_score || 0,
              passing_score: stage.passing_score,
              questions:
                idx === screeningIdx
                  ? screeningQuestions.map((q) => ({
                      id: q.id,
                      question: q.question,
                      expected_answer: q.expected_answer,
                      max_score: q.max_score,
                      question_type: q.question_type,
                      is_required: q.is_required,
                    }))
                  : [],
            })),
          );
          setError("");
        } catch (err) {
          console.error(err);
          setError("Failed to load job data for editing");
        } finally {
          setLoading(false);
        }
      };
      fetchJobData();
    }
  }, [isEditMode, job]);

  // ── Load templates (create mode) ──
  useEffect(() => {
    if (!isEditMode) {
      templateService
        .getTemplatesByStore(store_id)
        .then((res) => setTemplates(res.data.data || []))
        .catch(console.error);
    }
  }, [store_id, isEditMode]);

  // ── Auto-fill screening from template ──
  useEffect(() => {
    if (isEditMode || templateQuestions.length === 0) return;
    setStages((prev) => {
      // Resolved once against the list being mapped, so the questions land on
      // exactly one round. See questionRoundIndex.
      const target = questionRoundIndex(prev);
      return prev.map((stage, idx) => {
        if (idx === target && stage.questions.length === 0) {
          const qs = templateQuestions.map((q, idx) => ({
            question: q.question,
            expected_answer: q.expected_answer || "",
            max_score: q.max_score,
            question_type: q.question_type,
            is_required: q.is_required,
            tempId: Date.now() + idx,
          }));
          // max_score deliberately NOT recomputed from the questions any
          // more. It used to be set to their summed score, which is where the
          // invented "25" on a round came from; scores now live in the rubric.
          return { ...stage, questions: qs };
        }
        return stage;
      });
    });
  }, [templateQuestions, isEditMode]);

  // ── Skills ──
  const handleAddSkill = (e) => {
    if (e.key === "Enter" && skillInput.trim()) {
      const newSkills = [...skills, skillInput.trim()];
      setSkills(newSkills);
      setJobData((p) => ({ ...p, required_skills: newSkills.join(", ") }));
      setSkillInput("");
    }
  };
  const handleDeleteSkill = (skill) => {
    const newSkills = skills.filter((s) => s !== skill);
    setSkills(newSkills);
    setJobData((p) => ({ ...p, required_skills: newSkills.join(", ") }));
  };

  // ── Template change ──
  // Pulled out of the change handler so the mount-time prefill below can reuse
  // it: opening this form from a level has to fill the same fields the dropdown
  // would, and duplicating that once would guarantee the two drift apart.
  const applyTemplate = async (templateId) => {
    if (!templateId) {
      setTemplateQuestions([]);
      // Deselecting the template drops the JD it brought with it. Leaving it
      // attached would give the job a document the recruiter can no longer see
      // the origin of, and no obvious way to work out where it came from.
      setJd(null);
      setStages((prev) => {
        // Clears the round the prefill wrote to, whichever that is — this used
        // to hardcode index 0 and reset max_score to 25, so deselecting a
        // template left questions on a later round and stamped a score nobody
        // asked for.
        const target = questionRoundIndex(prev);
        return prev.map((stage, idx) =>
          idx === target ? { ...stage, questions: [] } : stage,
        );
      });
      return;
    }
    try {
      const res = await templateService.getTemplateById(templateId);
      const tpl = res.data.data;
      setJobData((prev) => ({
        ...prev,
        title: tpl.title || "",
        description: tpl.description || "",
        responsibilities: tpl.responsibilities || "",
        required_skills: tpl.required_skills || "",
        experience_required: tpl.experience_required || "",
        salary_min: tpl.salary_min || "",
        salary_max: tpl.salary_max || "",
      }));
      if (tpl.required_skills)
        setSkills(
          tpl.required_skills
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        );
      // Prefilled from the template, then owned by the job. The server stamps
      // the same value on create-from-template, so this is what the recruiter
      // sees rather than a new source of truth — and replacing it here
      // overrides the stamp without touching the template.
      setJd(
        tpl.jd_pdf_url
          ? { url: tpl.jd_pdf_url, name: tpl.jd_pdf_name }
          : null,
      );
      setTemplateQuestions(tpl.questions || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTemplateChange = async (e) => {
    if (isEditMode) return;
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    await applyTemplate(templateId);
  };

  // Opened from a template card on a level: select and apply that template once,
  // so the recruiter lands on a form that only needs the job-specific fields.
  useEffect(() => {
    if (isEditMode || !initialTemplateId) return;
    setSelectedTemplateId(initialTemplateId);
    applyTemplate(initialTemplateId);
    // Deliberately mount-only: re-running this would overwrite edits the user
    // has already made to the prefilled fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplateId, isEditMode]);

  // `list` is the round array to work against, defaulting to current state.
  //
  // IT MUST BE PASSED when called right after a type change. updateStage below
  // used to mutate the round object in place, so this function — reading
  // `stages` from its closure — happened to see the NEW type and behaved
  // correctly. That is accidental: the moment updateStage stopped mutating and
  // started copying, this would have read the OLD type, decided a different
  // round owned the questions, and silently filled nothing. Taking the list as
  // an argument makes the dependency explicit instead of load-bearing luck.
  const autoFillScreeningQuestions = (stageIndex, list = stages) => {
    if (isEditMode || templateQuestions.length === 0) return;
    // Only the round that owns the questions gets them, so retyping a second
    // round to AI/Call Interview cannot produce two copies on screen.
    if (stageIndex !== questionRoundIndex(list)) return;
    if (list[stageIndex]?.questions?.length !== 0) return;

    const qs = templateQuestions.map((q, idx) => ({
      question: q.question,
      expected_answer: q.expected_answer || "",
      max_score: q.max_score,
      question_type: q.question_type,
      is_required: q.is_required,
      tempId: Date.now() + idx,
    }));
    // max_score is deliberately NOT derived from the questions. Summing them is
    // where a round's invented score came from, and rounds are unscored now.
    setStages(
      list.map((st, i) => (i === stageIndex ? { ...st, questions: qs } : st)),
    );
  };

  // ── Stage helpers ──
  const addStage = () =>
    setStages([
      ...stages,
      {
        stage_name: "",
        stage_order: stages.length + 1,
        stage_type: "TECHNICAL",
        is_elimination_round: true,
        // Zero, like every other round. See the note in buildDefaultStages.
        max_score: 0,
        passing_score: 0,
        questions: [],
      },
    ]);

  // Copies the round it changes rather than mutating it in place. The previous
  // version wrote through to the object still held in state, which worked but
  // made the autofill below depend on that mutation — see the note there.
  const updateStage = (index, field, value) => {
    const updated = stages.map((st, i) => {
      if (i !== index) return st;
      const next = { ...st, [field]: value };
      // Retyping a round to Offer or Joining drops its elimination flag, and
      // retyping it back restores one. Without this, a round switched to Offer
      // keeps claiming it eliminates candidates.
      if (field === "stage_type") {
        next.is_elimination_round = !isScoreless(value);
      }
      return next;
    });
    setStages(updated);
    // The UPDATED list, not state — state has not been re-read yet at this
    // point, so passing it is what makes the new type visible.
    if (!isEditMode && field === "stage_type" && carriesQuestions(value)) {
      autoFillScreeningQuestions(index, updated);
    }
  };

  // Any round may go, as long as one remains.
  //
  // The first row used to be undeletable on the grounds that a job needs a
  // round for applicants to land on — but the intake writes people onto the
  // FIRST round BY ORDER, so deleting row one simply promotes row two. The
  // guard protected a position, not the property it was named for. "At least
  // one round" is the real invariant, and the server enforces it too.
  //
  // Removing a round that already has candidates on it is refused by the
  // server with a message naming them; nothing is lost locally in the meantime.
  const removeStage = (index) => {
    if (stages.length === 1) return;
    const updated = stages
      .filter((_, i) => i !== index)
      .map((s, idx) => ({ ...s, stage_order: idx + 1 }));
    setStages(updated);
  };

  // Move a round up or down.
  //
  // Step 2 has said "Drag to reorder stages" since before there was any way to
  // reorder them — there is no drag handler anywhere in this file, so the
  // instruction was simply untrue. These two buttons make it true, which is
  // cheaper and more accessible than drag-and-drop and works on a phone.
  //
  // Order is positional: the server takes round order from array position, so
  // stage_order is renumbered here and nothing else has to agree about it.
  const moveStage = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= stages.length) return;
    const updated = [...stages];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setStages(updated.map((s, idx) => ({ ...s, stage_order: idx + 1 })));
  };

  // The add/update/remove question helpers were here.
  //
  // Removed as dead code, not disabled. Every one of them opened with
  // `if (isEditMode) return;` and step 3 has no editor to call them from — it
  // renders each question as read-only text. They were the other half of a
  // question-authoring flow whose save path never existed (see handleSubmit),
  // so keeping them implied an editor somebody would go looking for.
  //
  // Questions are authored on the TEMPLATE, or on the interview-questions
  // screen once the job exists (interviewService.saveQuestions).

  const handleJobFieldChange = (e) =>
    setJobData({ ...jobData, [e.target.name]: e.target.value });

  // ── Validation ──
  const validateStep1 = () => {
    if (!jobData.title.trim()) return "Job title is required";
    if (!jobData.description.trim()) return "Description is required";
    return null;
  };
  // A round needs a name and nothing else.
  //
  // The score checks that used to live here demanded a max and a pass mark
  // above zero on every round, which forced a number onto rounds that have no
  // score at all — an offer, a joining date — and invited invented ones
  // everywhere else. Both fields are now always 0 and are not asked for; real
  // scoring lives in the interview rubric.
  const validateStep2 = () => {
    if (stages.length === 0) return "A job needs at least one round";
    for (let i = 0; i < stages.length; i++) {
      if (!stages[i].stage_name.trim()) return `Stage ${i + 1} name is required`;
    }
    return null;
  };
  // Nothing to validate. Step 3 REVIEWS the questions the template carries; it
  // has never been able to author or save them, so there is nothing here a
  // person could get wrong.
  //
  // It used to refuse to publish a job whose screening round had no questions,
  // which made a job with no template impossible to create at all — and the
  // questions it was insisting on could not be saved even when supplied. See
  // the note in handleSubmit.
  const validateStep3 = () => null;

  const handleNext = () => {
    let err = null;
    if (activeStep === 0) err = validateStep1();
    else if (activeStep === 1) err = validateStep2();
    else if (activeStep === 2) err = validateStep3();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setActiveStep((p) => p + 1);
  };

  const handleBack = () => {
    setActiveStep((p) => p - 1);
    setError("");
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (validateStep1() || validateStep2() || validateStep3()) {
      setError("Please fix validation errors before submitting.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (isEditMode) {
        const jobPayload = {
          store_id,
          ...jobData,
          salary_min: Number(jobData.salary_min) || 0,
          salary_max: Number(jobData.salary_max) || 0,
          vacancies: Number(jobData.vacancies),
          // Null rather than omitted, so removing the JD actually clears the
          // column. An omitted key leaves the old URL in place, and the form
          // would appear to have saved a removal it did not.
          jd_pdf_url: jd?.url || null,
          jd_pdf_name: jd?.name || null,
          total_rounds: stages.length,
          status: jobData.status,
        };
        await jobService.updateJob(job.id, jobPayload);

        // ── Rounds are RECONCILED, never recreated ────────────────────────
        //
        // `id` is carried through from the load (see the setStages above) and
        // is what makes this safe: the server updates that round in place, so
        // applications.current_stage_id, the interview rubric, the recorded
        // sessions and the movement history all keep pointing at a row that
        // still exists.
        //
        // This used to delete every round and create a fresh set. Each new
        // round got a NEW id, which detached all of the above — renaming a
        // round made every candidate on the job disappear from the pipeline
        // (it filters by stage id) and left "Send link" reporting that the
        // stage has no rubric. It was worse than that in practice: MySQL
        // REFUSED to delete a round a candidate sat on, deleteStage answered
        // 200 with statusCode 500 in the body so axios never threw, and the
        // recreate ran anyway — leaving the old rounds plus a duplicate set.
        //
        // Order comes from array position on the server, so stage_order is not
        // sent. A round the user removed is simply absent from this list.
        await jobService.saveJobStages(
          job.id,
          stages.map((s) => ({
            // Present for rounds that already exist, absent for ones just
            // added in the wizard. That distinction is the whole mechanism.
            ...(s.id ? { id: s.id } : {}),
            stage_name: s.stage_name,
            stage_type: s.stage_type,
            is_elimination_round: s.is_elimination_round,
            total_score: s.max_score,
            passing_score: s.passing_score,
          })),
        );
        // ── No question write here, deliberately ──────────────────────────
        //
        // This block used to call jobService.createScreeningQuestions(). That
        // function DOES NOT EXIST — it is commented out in jobService.js — and
        // POST /screening-questions/create has no route on the server either.
        // So the call threw "createScreeningQuestions is not a function", the
        // outer catch reported "Failed to save job", and the job and its rounds
        // had ALREADY been written. Saving a job that had any questions on it
        // looked like a total failure and invited a retry that made a duplicate.
        //
        // Nothing is lost by removing it: step 3 only ever DISPLAYED the
        // template's questions, and the questions the AI interview actually
        // asks live in `screening_questions` by job_id, authored on the
        // interview-questions screen (interviewService.saveQuestions). This
        // wizard was never their home.
        if (onUpdated) onUpdated();
      } else {
        if (selectedTemplateId) {
          await jobService.createJobFromTemplate(
            selectedTemplateId,
            {
              title: jobData.title,
              description: jobData.description,
              responsibilities: jobData.responsibilities,
              required_skills: jobData.required_skills,
              experience_required: jobData.experience_required,
              salary_min: jobData.salary_min,
              salary_max: jobData.salary_max,
              whatsapp_group_link: jobData.whatsapp_group_link,
              google_form_link: jobData.google_form_link,
              google_sheet_id: jobData.google_sheet_id,
              // Always sent, including as null.
              //
              // The server inherits the template's JD only when these keys are
              // ABSENT, so sending them makes this form authoritative: whatever
              // is on screen is what the job gets. That is what allows the
              // recruiter to remove a template's JD for one opening — omitting
              // the keys on removal would have the server helpfully put it
              // back, and they would find out when a candidate received it.
              jd_pdf_url: jd?.url || null,
              jd_pdf_name: jd?.name || null,
              notify_cc: jobData.notify_cc,
              mail_signature: jobData.mail_signature,
              status: jobData.status,
              created_by: user_id,
              // Stamped so the job records where it was opened. Omitted when
              // this form was not opened from a level, in which case the server
              // falls back to the template's own assignment.
              ...(deptContext?.department_id
                ? {
                    department_id: deptContext.department_id,
                    dept_node_id: deptContext.dept_node_id || null,
                  }
                : {}),
            },
            stages.map((s) => ({
              stage_name: s.stage_name,
              stage_order: s.stage_order,
              stage_type: s.stage_type,
              is_elimination_round: s.is_elimination_round,
              total_score: s.max_score,
              passing_score: s.passing_score,
            })),
          );
        } else {
          const jobRes = await jobService.createJob({
            store_id,
            ...(deptContext?.department_id
              ? {
                  department_id: deptContext.department_id,
                  dept_node_id: deptContext.dept_node_id || null,
                }
              : {}),
            ...jobData,
            salary_min: Number(jobData.salary_min) || 0,
            salary_max: Number(jobData.salary_max) || 0,
            vacancies: Number(jobData.vacancies),
            jd_pdf_url: jd?.url || null,
            jd_pdf_name: jd?.name || null,
            total_rounds: stages.length,
            created_by: user_id,
          });
          const newJobId = jobRes.data.data.id;
          await jobService.createHiringStages(
            store_id,
            newJobId,
            stages.map((s) => ({
              stage_name: s.stage_name,
              stage_order: s.stage_order,
              stage_type: s.stage_type,
              is_elimination_round: s.is_elimination_round,
              max_score: s.max_score,
              passing_score: s.passing_score,
            })),
          );
          // No question write here either — same reason as the edit path
          // above. The call this used to make does not exist on the client or
          // the server, and it took the whole save down with it.
        }
        if (onCreated) onCreated();
      }
    } catch (err) {
      console.error("Job save error:", err);
      setError(
        err.response?.data?.statusMessage ||
          "Failed to save job. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ fontFamily: fontFam, px: 24 }}>
      {/* ── Top stepper bar ── */}
      <Box sx={{ mb: 1.5 }}>
        <CustomStepper activeStep={activeStep} steps={steps} />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            borderRadius: "10px",
            fontSize: 12,
            fontFamily: fontFam,
          }}
        >
          {error}
        </Alert>
      )}

      {/* ══════════════════════════ STEP 1 ══════════════════════════ */}
      {activeStep === 0 && (
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            p: { xs: 2.5, sm: 3.5 },
          }}
        >
          <Typography
            sx={{
              fontFamily: fontFam,
              fontWeight: 500,
              fontSize: 14,
              color: "#515152",
              mb: 1,
            }}
          >
            Fill in the details to publish your new vacancy to job boards.
          </Typography>

          {/* Where this job sits in the hierarchy, when it was opened from a
              level. Read-only on purpose: the level was chosen by navigating to
              it, so offering it again as an editable field here would invite a
              job whose breadcrumb disagrees with where it was created. */}
          {!isEditMode && deptContext?.department_id && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                flexWrap: "wrap",
                bgcolor: TEAL[50],
                border: `1px solid ${TEAL[100]}`,
                borderRadius: "10px",
                px: 2,
                py: 1.3,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: fontFam,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: TEAL[700],
                }}
              >
                Opening under
              </Typography>
              <Typography
                sx={{ fontFamily: fontFam, fontSize: 13.5, color: "#0f172a" }}
              >
                {deptContext.breadcrumb || deptContext.department_name}
              </Typography>
            </Box>
          )}

          {/* Admin Shortcut Banner */}
          {!isEditMode && !deptContext?.dept_node_id && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: TEAL[50],
                border: `1px solid ${TEAL[100]}`,
                borderRadius: "10px",
                px: 2,
                py: 1.4,
                mb: 3,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    bgcolor: TEAL[100],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: TEAL[600],
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  ✦
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: fontFam,
                      fontWeight: 700,
                      fontSize: 12.5,
                      color: TEAL[700],
                    }}
                  >
                    Admin Shortcut
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fontFam,
                      fontSize: 11.5,
                      color: TEAL[600],
                    }}
                  >
                    Save time by loading from a pre-defined template.
                  </Typography>
                </Box>
              </Box>
              <TextField
                select
                size="small"
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                SelectProps={{
                  displayEmpty: true,

                  // prevents weird jump/up animation feeling
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        mt: 1,
                        borderRadius: "14px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                        border: "1px solid #fde68a",
                      },
                    },

                    MenuListProps: {
                      sx: {
                        py: 1,
                      },
                    },

                    // keeps dropdown below
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },

                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  },

                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "#d97706",
                            fontWeight: 600,
                          }}
                        >
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/8/8f/Google-gemini-icon.svg"
                            alt="Gemini"
                            style={{
                              width: 16,
                              height: 16,
                              color: "#d97706",
                              fill: "#d97706",
                            }}
                          />
                          Load Template
                        </Box>
                      );
                    }

                    const selectedTemplate = templates.find(
                      (tpl) => tpl.id === selected,
                    );

                    return selectedTemplate?.template_name || "";
                  },
                }}
                sx={{
                  minWidth: 240,

                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    fontSize: 13,
                    fontFamily: fontFam,
                    background:
                      "linear-gradient(180deg, #fffdf7 0%, #fff7ed 100%)",
                    transition: "all 0.2s ease",

                    "& fieldset": {
                      borderColor: "#fdba74",
                    },

                    "&:hover fieldset": {
                      borderColor: "#fb923c",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "#f59e0b",
                      borderWidth: "1.5px",
                      boxShadow: "0 0 0 4px rgba(245,158,11,0.12)",
                    },
                  },

                  "& .MuiInputLabel-root": {
                    color: "#d97706",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: fontFam,
                  },

                  "& .MuiSelect-select": {
                    py: "10px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: fontFam,
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    fontSize: 13,
                    fontFamily: fontFam,
                    color: "#92400e",
                  }}
                >
                  ✨ Select Template
                </MenuItem>

                {templates.map((tpl) => (
                  <MenuItem
                    key={tpl.id}
                    value={tpl.id}
                    sx={{
                      fontSize: 13,
                      fontFamily: fontFam,
                      py: 1.2,

                      "&:hover": {
                        backgroundColor: "#fff7ed",
                      },

                      "&.Mui-selected": {
                        backgroundColor: "#ffedd5",
                      },

                      "&.Mui-selected:hover": {
                        backgroundColor: "#fed7aa",
                      },
                    }}
                  >
                    ✨ {tpl.template_name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <Grid container spacing={2.5}>
            {/* ================= TOP SECTION ================= */}
            <Grid item xs={12}>
              <Grid
                container
                spacing={2}
                // sx={{
                //   bgcolor: "#eeeeeed7",
                //   borderRadius: 0.5,
                //   p: 1,
                //   pt: 0,
                //   pb:
                //   overflow: "hidden",

                //   // FIX ALIGNMENT
                //   width: "100%",
                //   mx: 0,
                // }}
              >
                {/* LEFT SIDE */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      height: "100%",
                    }}
                  >
                    {/* Job Title */}
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        name="title"
                        value={jobData.title}
                        onChange={handleJobFieldChange}
                        placeholder="Job Title (e.g. Senior Software Engineer)"
                        required
                        sx={inputSx}
                      />
                    </Box>

                    {/* Experience */}
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        name="experience_required"
                        value={jobData.experience_required}
                        onChange={handleJobFieldChange}
                        placeholder="Experience Level (e.g. 0 - 4 Years)"
                        sx={inputSx}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* RIGHT SIDE */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignContent: "flex-start",
                      gap: 0.7,
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      px: 1.2,
                      py: 1,
                      bgcolor: "#fff",
                      minHeight: 88,

                      "&:focus-within": {
                        borderColor: TEAL[500],
                        outline: `1.5px solid ${TEAL[500]}`,
                      },
                    }}
                  >
                    {skills.map((skill) => (
                      <Chip
                        key={skill}
                        label={skill}
                        size="small"
                        onDelete={() => handleDeleteSkill(skill)}
                        sx={{
                          fontFamily: fontFam,
                          fontSize: 11.5,
                          height: 26,
                          bgcolor: TEAL[50],
                          color: TEAL[700],
                          border: `1px solid ${TEAL[200]}`,

                          "& .MuiChip-deleteIcon": {
                            fontSize: 13,
                            color: TEAL[400],
                          },
                        }}
                      />
                    ))}

                    <input
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyDown={handleAddSkill}
                      placeholder="Required Skills"
                      style={{
                        border: "none",
                        outline: "none",
                        fontSize: 13,
                        fontFamily: fontFam,
                        color: "#1e293b",
                        background: "transparent",
                        minWidth: 120,
                        flex: 1,
                        padding: "4px 2px",
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* ================= DESCRIPTION ================= */}
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>
                Description <span style={{ color: "#ef4444" }}>*</span>
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                name="description"
                value={jobData.description}
                onChange={handleJobFieldChange}
                required
                sx={inputSx}
              />
            </Grid>

            {/* ================= RESPONSIBILITIES ================= */}
            <Grid item xs={12} sm={6}>
              <Typography component="label" sx={labelSx}>
                Responsibilities
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                name="responsibilities"
                value={jobData.responsibilities}
                onChange={handleJobFieldChange}
                sx={inputSx}
              />
            </Grid>

            {/* ================= SALARY ================= */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  p: 2.5,
                  bgcolor: "#fafbfc",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 15 }}>💰</Typography>

                  <Typography
                    sx={{
                      fontFamily: fontFam,
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#374151",
                    }}
                  >
                    Annual Salary Range
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography component="label" sx={labelSx}>
                      Minimum Salary
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      name="salary_min"
                      type="number"
                      value={jobData.salary_min}
                      onChange={handleJobFieldChange}
                      placeholder="80,000"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                              ₹
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography component="label" sx={labelSx}>
                      Maximum Salary
                    </Typography>

                    <TextField
                      fullWidth
                      size="small"
                      name="salary_max"
                      type="number"
                      value={jobData.salary_max}
                      onChange={handleJobFieldChange}
                      placeholder="120,000"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                              ₹
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* ================= LINKS ================= */}
            <Grid item xs={12} sm={6}>
              <Grid container spacing={1.6}>
                <Grid item xs={12}>
                  <TextField
                    label="WhatsApp Group Link"
                    fullWidth
                    size="small"
                    name="whatsapp_group_link"
                    value={jobData.whatsapp_group_link}
                    onChange={handleJobFieldChange}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Google Form Link"
                    fullWidth
                    size="small"
                    name="google_form_link"
                    value={jobData.google_form_link}
                    onChange={handleJobFieldChange}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Google Sheet ID (The ID from your Google Sheet URL)"
                    fullWidth
                    size="small"
                    name="google_sheet_id"
                    value={jobData.google_sheet_id}
                    onChange={handleJobFieldChange}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* ================= CANDIDATE EMAIL ================= */}
            {/*
                Everything that decides what a candidate actually receives when
                somebody presses "Send link", in one block rather than scattered
                among the job's own details — because these three are read
                together, at the moment somebody asks "what did we send them?".

                All three are optional. A job that leaves them empty sends
                exactly what it sent before this section existed: the
                deployment's own CC, the generic sign-off, and no attachment.
            */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2.5 }} />
              <Typography
                sx={{
                  fontFamily: fontFam,
                  fontWeight: 700,
                  fontSize: 13,
                  color: "#0f172a",
                  mb: 0.4,
                }}
              >
                Candidate email
              </Typography>
              <Typography
                sx={{
                  fontFamily: fontFam,
                  fontSize: 11.5,
                  color: "#64748b",
                  mb: 2,
                }}
              >
                What goes out with the interview link for this position.
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <JdAttachmentField
                    value={jd}
                    onChange={setJd}
                    helperText="Attached to every interview invitation sent for this position, and downloadable from the job page."
                    inheritedNote={
                      !isEditMode && selectedTemplateId
                        ? "Came from the template. Replacing it here changes this job only."
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <EmailListField
                    value={jobData.notify_cc}
                    onChange={(value) =>
                      setJobData((p) => ({ ...p, notify_cc: value }))
                    }
                    label="Copy these people (CC)"
                    helperText="Copied on candidate email for this position — the candidate can see them and reply to all. The department head is blind-copied automatically, so there is no need to add them here."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography component="label" sx={labelSx}>
                    Email signature
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    name="mail_signature"
                    value={jobData.mail_signature}
                    onChange={handleJobFieldChange}
                    placeholder={
                      "Priya Menon\nTalent Acquisition, Kumudu\n+91 98765 43210"
                    }
                    helperText="Signs off candidate email for this position. Leave empty to use the generic company sign-off."
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              pt: 3,
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{ fontSize: 11.5, color: "#94a3b8", fontFamily: fontFam }}
            >
              Step 1 of {steps.length}: Primary Information
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                onClick={onCancel}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontFamily: fontFam,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#374151",
                  border: "1px solid #e2e8f0",
                  px: 2.5,
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: fontFam,
                  fontSize: 13,
                  px: 3,
                  bgcolor: TEAL[700],
                  color: "#fff",
                  boxShadow: "none",
                  "&:hover": { bgcolor: TEAL[800], boxShadow: "none" },
                }}
              >
                Next Step →
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* ══════════════════════════ STEP 2 ══════════════════════════ */}
      {activeStep === 1 && (
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            p: { xs: 2.5, sm: 3.5 },
          }}
        >
          <Typography
            sx={{
              fontFamily: fontFam,
              fontWeight: 700,
              fontSize: 17,
              color: "#0f172a",
              mb: 0.4,
            }}
          >
            Step 2: Hiring Stages
          </Typography>
          <Typography
            sx={{
              fontFamily: fontFam,
              fontSize: 12.5,
              color: "#6b7280",
              mb: 3,
            }}
          >
            Every job starts with the six standard rounds. Rename them, change
            a type, reorder with the arrows, or add and remove rounds.
          </Typography>

          {/* Two conditions that produce a confusing failure LATER, surfaced
              here where the rounds are being decided.
              Neither blocks saving — a job that never interviews is legitimate,
              and so is one that interviews twice — but discovering it at
              "Send link", days later, on a candidate who is waiting, is the
              outcome this avoids. interviewController resolves the job's FIRST
              AI_INTERVIEW round by order, which is why two is ambiguous. */}
          {aiRoundCount === 0 && (
            <Alert
              severity="warning"
              sx={{ borderRadius: "10px", fontSize: 12, mb: 2 }}
            >
              No "AI / Call Interview" round. Interview links can only be issued
              from that round, so this job will not be able to send any. Add one
              if you want AI interviews.
            </Alert>
          )}
          {aiRoundCount > 1 && (
            <Alert
              severity="warning"
              sx={{ borderRadius: "10px", fontSize: 12, mb: 2 }}
            >
              {aiRoundCount} rounds are set to "AI / Call Interview". Interview
              links always use the earliest one, so the others will never issue
              a link.
            </Alert>
          )}

          {stages.map((stage, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                bgcolor: "#fff",
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                {/* Was a decorative drag handle beside the words "Drag to
                    reorder" — with no drag handler in the file. Real controls
                    instead. */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                    mb: 0.3,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => moveStage(idx, -1)}
                    disabled={idx === 0}
                    title="Move this round earlier"
                    sx={{ p: 0.1, color: "#94a3b8", "&:hover": { color: TEAL[600] } }}
                  >
                    <KeyboardArrowUpIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: fontFam,
                      color: "#94a3b8",
                      textAlign: "center",
                      lineHeight: 1,
                    }}
                  >
                    {idx + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => moveStage(idx, 1)}
                    disabled={idx === stages.length - 1}
                    title="Move this round later"
                    sx={{ p: 0.1, color: "#94a3b8", "&:hover": { color: TEAL[600] } }}
                  >
                    <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
                <Grid
                  container
                  spacing={1.5}
                  alignItems="flex-end"
                  sx={{ flex: 1 }}
                >
                  <Grid item xs={12} sm={7}>
                    <Typography sx={{ ...labelSx }}>Stage Name</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={stage.stage_name}
                      onChange={(e) =>
                        updateStage(idx, "stage_name", e.target.value)
                      }
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Typography sx={{ ...labelSx }}>Type</Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={stage.stage_type}
                      onChange={(e) =>
                        updateStage(idx, "stage_type", e.target.value)
                      }
                      sx={inputSx}
                    >
                      {stagesTypes.map((t) => (
                        <MenuItem
                          key={t}
                          value={t}
                          sx={{ fontSize: 12, fontFamily: fontFam }}
                        >
                          {/* The label, not the enum value. This dropdown used
                              to read "AI_INTERVIEW" and "HR_ROUND" — the
                              database's vocabulary, not a recruiter's. */}
                          {STAGE_TYPE_LABELS[t] || t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {/* The Max and Passing inputs were here.
                      Removed rather than defaulted: they were required on every
                      round, meaningless on an Offer or a Joining, and invented
                      everywhere else — nobody knew what "max 50, passing 30"
                      meant for an F2F panel, so a number got typed to clear the
                      validation. Both are now always 0, and real scoring lives
                      in the interview rubric where a threshold is actually
                      applied. */}
                </Grid>
                {/* Elimination + delete */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                    gap: 0.3,
                    pb: 0.3,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontFamily: fontFam,
                      color: "#64748b",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {/* Offer and Joining are not rounds anybody fails, so the
                        label would be asking a question with no meaning. */}
                    {isScoreless(stage.stage_type) ? " " : "Elimination Round"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Switch
                      size="small"
                      checked={
                        isScoreless(stage.stage_type)
                          ? false
                          : Boolean(stage.is_elimination_round)
                      }
                      onChange={(e) =>
                        updateStage(
                          idx,
                          "is_elimination_round",
                          e.target.checked,
                        )
                      }
                      // Hidden rather than disabled for Offer and Joining: a
                      // greyed-out toggle still invites "why can I not set
                      // this?", and the answer is that the concept does not
                      // apply to them. `visibility` rather than unmounting, so
                      // the delete button beside it does not shift position
                      // from row to row.
                      sx={{
                        ...(isScoreless(stage.stage_type)
                          ? { visibility: "hidden" }
                          : null),
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { bgcolor: TEAL[500] },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeStage(idx)}
                      disabled={stages.length === 1 || idx === 0}
                      sx={{
                        color: "#94a3b8",
                        "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" },
                        p: 0.4,
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}

          {/* Add Stage */}
          {/* Enabled while editing, not just while creating.
              It was inert in edit mode because the old save path could only
              delete every round and recreate the set — there was no way to add
              one to a job that already had candidates. Saving now reconciles by
              id (jobService.saveJobStages), so a round added here is inserted
              and the existing ones are left alone. */}
          <Box
            onClick={addStage}
            sx={{
              border: `2px dashed ${TEAL[200]}`,
              borderRadius: "12px",
              py: 1.8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              cursor: "pointer",
              color: TEAL[500],
              fontFamily: fontFam,
              fontWeight: 600,
              fontSize: 13,
              mt: 1,
              transition: "all 0.18s",
              "&:hover": { bgcolor: TEAL[50], borderColor: TEAL[400] },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} /> Add New Hiring Stage
          </Box>

          {/* Pro-tip */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mt: 3,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              px: 2,
              py: 1.5,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: TEAL[400],
                mt: 0.6,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontFamily: fontFam,
                fontSize: 12,
                color: "#475569",
                lineHeight: 1.6,
              }}
            >
              {/* This block used to promise that failing an elimination round
                  moved a candidate to Rejected automatically, and that the
                  rejection email was customisable in the final step. Neither is
                  true: `is_elimination_round` is written to the database and
                  read by nothing, and there is no rejection-email step. Two
                  invented features, stated as fact, in the one place somebody
                  reads before setting their pipeline up. */}
              <strong>How rounds are used:</strong> applicants land on the first
              round. "AI/Call Interview" is the round that issues interview
              links, so keep one. Moving a candidate between rounds is done from
              the candidate list — rounds are not scored here; the interview
              rubric holds the scoring.
            </Typography>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              pt: 3,
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{ fontSize: 11.5, color: "#94a3b8", fontFamily: fontFam }}
            >
              Step 2 of {steps.length}: Hiring Stages
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                onClick={handleBack}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontFamily: fontFam,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#374151",
                  border: "1px solid #e2e8f0",
                  px: 2.5,
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                ← Back to Details
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: fontFam,
                  fontSize: 13,
                  px: 3,
                  bgcolor: TEAL[700],
                  color: "#fff",
                  boxShadow: "none",
                  "&:hover": { bgcolor: TEAL[800], boxShadow: "none" },
                }}
              >
                Proceed to Access →
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* ══════════════════════════ STEP 3 ══════════════════════════ */}
      {activeStep === 2 && (
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            p: { xs: 2.5, sm: 3.5 },
          }}
        >
          {/* Header */}
          <Typography
            sx={{
              fontFamily: fontFam,
              fontWeight: 700,
              fontSize: 17,
              color: "#0f172a",
              mb: 0.4,
            }}
          >
            Step 3: Review & Publish
          </Typography>

          <Typography
            sx={{
              fontFamily: fontFam,
              fontSize: 12.5,
              color: "#6b7280",
              mb: 0.6,
            }}
          >
            Review the questions this job inherited from its template. They are
            edited on the template, or on the interview-questions screen once
            the job exists — not here. Optional: a job publishes fine with none.
          </Typography>

          {/* Questions */}
          {stages.map(
            (stage, sIdx) =>
              sIdx === questionRoundIndex(stages) && (
                <Box key={sIdx} sx={{ mb: 4 }}>
                  {/* Stage Title */}

                  <Divider sx={{ mb: 2.5, borderColor: "#edf2f7" }} />

                  {/* Question List */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {stage.questions.map((q, qIdx) => (
                      <Box
                        key={q.id || q.tempId || qIdx}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          p: 2,
                          borderRadius: "12px",
                          border: "1px solid #e2e8f0",
                          bgcolor: "#fafbfc",
                          transition: "all 0.2s ease",

                          "&:hover": {
                            borderColor: TEAL[200],
                            bgcolor: "#ffffff",
                          },
                        }}
                      >
                        {/* Number */}
                        <Box
                          sx={{
                            minWidth: 28,
                            height: 28,
                            borderRadius: "8px",
                            bgcolor: TEAL[50],
                            border: `1px solid ${TEAL[100]}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: fontFam,
                            color: TEAL[700],
                          }}
                        >
                          {qIdx + 1}
                        </Box>

                        {/* Question */}
                        <Typography
                          sx={{
                            fontFamily: fontFam,
                            fontSize: 13.5,
                            lineHeight: 1.7,
                            color: "#334155",
                            fontWeight: 500,
                            flex: 1,
                            pt: "2px",
                          }}
                        >
                          {q.question}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ),
          )}

          {/* Empty State */}
          {/* Two different nothings, and they used to share one message that
              was wrong for both — it said every job needs a round that asks
              questions, which is not true and was not enforceable from here. */}
          {questionRoundIndex(stages) === -1 ? (
            <Alert severity="info" sx={{ borderRadius: "10px", fontSize: 12 }}>
              This job has no Resume Screening or AI/Call Interview round, so
              there is nowhere for screening questions to be asked. That is a
              valid setup — you can publish and add a round later.
            </Alert>
          ) : (
            stages[questionRoundIndex(stages)]?.questions?.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: "10px", fontSize: 12 }}>
                No screening questions yet. You can publish the job now and
                author them on the interview-questions screen, or pick a
                template that carries them.
              </Alert>
            )
          )}

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              pt: 3,
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                color: "#94a3b8",
                fontFamily: fontFam,
              }}
            >
              Step 3 of {steps.length}: Screening Questions
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                onClick={handleBack}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontFamily: fontFam,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#374151",
                  border: "1px solid #e2e8f0",
                  px: 2.5,

                  "&:hover": {
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                ← Back
              </Button>

              <Button
                variant="contained"
                // The final step must SUBMIT, not advance. It called handleNext,
                // which stepped activeStep from 2 to 3 — a state with no render
                // branch — so the form blanked out, every step showed a tick, and
                // no request was ever made. handleSubmit was dead code.
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: fontFam,
                  fontSize: 13,
                  px: 3,
                  bgcolor: TEAL[700],
                  color: "#fff",
                  boxShadow: "none",

                  "&:hover": {
                    bgcolor: TEAL[800],
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Publishing…" : "Proceed to Publish →"}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default JobCreationForm;
